import * as React from "react"
import { SidebarCollapsibleTrigger, useSidebar } from "@shadcn/components/layouts/Sidebar/Sidebar"

interface NavigableCollapsibleTriggerProps
	extends Omit<React.ComponentProps<typeof SidebarCollapsibleTrigger>, "showIndicator"> {
	/**
	 * The route to navigate to when clicked in collapsed state
	 */
	href: string
	/**
	 * Whether this item is currently active
	 */
	isActive?: boolean
}

/**
 * A collapsible trigger that expands the sidebar and shows children when clicked
 * in collapsed (icon) mode, and toggles the submenu when the sidebar is expanded.
 *
 * Includes a chevron indicator that automatically hides when the sidebar is collapsed.
 */
export const NavigableCollapsibleTrigger = React.forwardRef<
	React.ComponentRef<typeof SidebarCollapsibleTrigger>,
	NavigableCollapsibleTriggerProps
>(({ href, isActive, onClick, onAuxClick, children, ...props }, ref) => {
	const { state, setOpen } = useSidebar()

	const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		// When sidebar is collapsed (icon mode), expand the sidebar first
		if (state === "collapsed") {
			setOpen(true)
		}
		// Let the default CollapsibleTrigger behavior handle toggling to show children
		onClick?.(e)
	}

	const handleAuxClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		// Handle middle-click, ctrl+click, cmd+click for opening in new tabs
		if (e.button === 1 || e.ctrlKey || e.metaKey) {
			e.preventDefault()
			window.open(href, "_blank")
		}
		onAuxClick?.(e)
	}

	return (
		<SidebarCollapsibleTrigger
			ref={ref}
			onClick={handleClick}
			onAuxClick={handleAuxClick}
			data-active={isActive}
			showIndicator={true}
			{...props}>
			{children}
		</SidebarCollapsibleTrigger>
	)
})

NavigableCollapsibleTrigger.displayName = "NavigableCollapsibleTrigger"
